import { h, Fragment, Component, render as preactRender } from "preact";
import { differenceInDays } from "date-fns";
import PriceDetailsModal from "./price-details-modal";

function amountToHuman(val) {
  if (
    typeof window !== "undefined" &&
    typeof window.amountToHuman === "function"
  ) {
    return window.amountToHuman(val);
  }
  const locale = global.Translator?.locale === "en" ? "en-GB" : "de-DE";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
  }).format(val || 0);
}

const toYmd = (date) => {
  if (!date) return "";
  if (typeof date === "string") return date.slice(0, 10);
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
  }
  return "";
};

class BookingCalculation extends Component {
  constructor(props) {
    super(props);
    this.priceUrl = props.bcWrap?.dataset.priceUrl || "";
    this.abortController = null;

    this.state = {
      gross: 0,
      net: 0,
      netPerNight: 0,
      rentalTotal: 0,
      perNight: 0,
      cleaning: 0,
      discounts: { total: 0, items: {} },
      taxes: 0,
      cityTax: 0,
      kurtaxe: 0,
      hasIncludedTaxes: false,
      hasTourismTax: false,
      businessOnly: false,
      adults: 1,
      children: 0,
      childrenAges: [],
      dogs: 0,
      dates: [],
      days: 0,
      rentalUnitId: "",
      unit: {},
      unitsNeeded: 1,
      seasons: [],
      services: [],
      infoServices: [],
      utilities: [],
    };

    this.handleDateChange = this.handleDateChange.bind(this);
    this.handleAdultsChange = this.handleAdultsChange.bind(this);
    this.handleChildrenChange = this.handleChildrenChange.bind(this);
    this.handleChildrenAgesChange = this.handleChildrenAgesChange.bind(this);
    this.handleDogsChange = this.handleDogsChange.bind(this);
    this.handleUnitChange = this.handleUnitChange.bind(this);
  }

  componentDidMount() {
    this.dateInput = document.querySelector(
      "#checkinCheckoutDates, #booking_dates",
    );
    this.adultsInput = document.querySelector("#adults, #booking_adults");
    this.childrenInput = document.querySelector("#children, #booking_children");
    this.dogsInput = document.querySelector("#dogs, #booking_dogs");
    this.rentalUnitSelect = document.querySelector(
      "#rentalUnitId, #rental_unit_id, #booking_rental_unit_id, #booking_rentalUnitId",
    );

    let dates = [];
    let days = 0;
    if (this.dateInput && this.dateInput._flatpickr) {
      dates = this.dateInput._flatpickr.selectedDates || [];
      if (dates.length === 2) {
        days = differenceInDays(dates[1], dates[0]);
      }
    }

    const adults = this.adultsInput
      ? parseInt(this.adultsInput.value, 10) || 1
      : 1;
    const children = this.childrenInput
      ? parseInt(this.childrenInput.value, 10) || 0
      : 0;
    const dogs = this.dogsInput
      ? parseInt(this.dogsInput.value, 10) || 0
      : 0;
    const rentalUnitId = this.rentalUnitSelect?.value || "";

    this.setState(
      {
        dates,
        days,
        adults,
        children,
        dogs,
        rentalUnitId,
      },
      () => this.calculatePrice()
    );

    this.addListeners();

    this._handlePriceRecalc = (e) => {
      const {
        adults: evAdults,
        children: evChildren,
        childrenAges,
        dogs: evDogs,
        days: evDays,
        dates: evDates,
        rentalUnitId: evUnitId,
      } = e.detail || {};
      const updates = {};
      if (evAdults !== undefined) updates.adults = parseInt(evAdults, 10) || 1;
      if (evChildren !== undefined)
        updates.children = parseInt(evChildren, 10) || 0;
      if (childrenAges !== undefined) updates.childrenAges = childrenAges;
      if (evDogs !== undefined) updates.dogs = parseInt(evDogs, 10) || 0;
      if (evDays !== undefined) updates.days = evDays;
      if (evDates !== undefined) updates.dates = evDates;
      if (evUnitId !== undefined) updates.rentalUnitId = String(evUnitId);
      this.setState(updates, () => this.calculatePrice());
    };
    window.addEventListener("p4g-price-recalc", this._handlePriceRecalc);
  }

  componentWillUnmount() {
    this.removeListeners();
    if (this._handlePriceRecalc) {
      window.removeEventListener("p4g-price-recalc", this._handlePriceRecalc);
    }
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  async calculatePrice() {
    if (!this.priceUrl || this.state.dates.length < 2 || this.state.days <= 0) {
      this.applyResult({ gross: 0, days: 0 });
      return;
    }

    const fromDate = toYmd(this.state.dates[0]);
    const toDate = toYmd(this.state.dates[1]);
    if (!fromDate || !toDate) {
      this.applyResult({ gross: 0, days: 0 });
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    const params = new URLSearchParams({
      fromDate,
      toDate,
      adults: String(this.state.adults || 1),
      children: String(this.state.children || 0),
      dogs: String(this.state.dogs || 0),
    });

    if (this.state.rentalUnitId) {
      params.set("rental_unit_id", String(this.state.rentalUnitId));
    }
    (this.state.childrenAges || []).forEach((age) => {
      if (age !== null && age !== undefined && !Number.isNaN(age)) {
        params.append("childrenAges[]", String(age));
      }
    });

    try {
      const response = await fetch(`${this.priceUrl}?${params.toString()}`, {
        method: "GET",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        this.applyResult({ gross: 0, days: 0 });
        return;
      }

      const data = await response.json();
      this.applyResult(data);
    } catch (err) {
      if (err?.name !== "AbortError") {
        console.error("Failed to fetch price calculation", err);
      }
    }
  }

  applyResult(data) {
    const gross = parseFloat(data.gross || 0);
    const nextState = {
      gross,
      net: parseFloat(data.net || 0),
      netPerNight: parseFloat(data.netPerNight || 0),
      rentalTotal: parseFloat(data.rentalTotal || 0),
      perNight: parseFloat(data.perNight || 0),
      cleaning: parseFloat(data.cleaning || 0),
      discounts: data.discounts || { total: 0, items: {} },
      taxes: parseFloat(data.taxes || 0),
      cityTax: parseFloat(data.cityTax || 0),
      kurtaxe: parseFloat(data.kurtaxe || 0),
      hasIncludedTaxes: !!data.hasIncludedTaxes,
      hasTourismTax: !!data.hasTourismTax,
      businessOnly: !!data.businessOnly,
      unitsNeeded: data.unitsNeeded || 1,
      seasons: data.seasons || [],
      services: data.services || [],
      infoServices: data.infoServices || [],
      utilities: data.utilities || [],
      unit: data.unit || {},
    };

    this.setState(nextState, () => {
      this.updatePriceLabel(gross);
      this.updateBookingPriceSummary(gross);
    });
  }

  updatePriceLabel(gross) {
    const mobilePriceEls = document.querySelectorAll(
      ".js-booking-total-price-mobile"
    );
    if (mobilePriceEls.length > 0 && gross > 0) {
      const formatted = amountToHuman(gross);
      mobilePriceEls.forEach((el) => {
        el.textContent = formatted;
      });
    }

    const priceEls = document.querySelectorAll(".js-booking-total-price");
    const containerEls = document.querySelectorAll(
      ".js-booking-price-container"
    );
    if (gross > 0) {
      const formatted = amountToHuman(gross);
      priceEls.forEach((el) => {
        el.textContent = formatted;
      });
      containerEls.forEach((el) => {
        el.style.display = "";
      });
    } else {
      containerEls.forEach((el) => {
        el.style.display = "none";
      });
    }
  }

  updateBookingPriceSummary(gross) {
    const containers = document.querySelectorAll(
      "#booking-price-view, .js-booking-price-view"
    );
    if (containers.length === 0) return;

    containers.forEach((container) => {
      const summary = container.closest(
        "#booking-price-summary, .js-booking-price-summary"
      );
      const fallback = summary?.querySelector("#booking-price-fallback");

      if (fallback) {
        fallback.textContent = "";
        fallback.classList.add("hidden");
      }

      if (gross <= 0 || this.state.days === 0) {
        if (summary) summary.style.display = "none";
        preactRender(null, container);
        return;
      }

      if (summary) summary.style.display = "";

      const t = (key, params = {}) => {
        return global.Translator?.trans
          ? global.Translator.trans(key, params, "js")
          : key;
      };

      preactRender(
        h(
          Fragment,
          null,
          h(
            "div",
            {
              className:
                "flex justify-between items-baseline flex-wrap gap-2 text-brandcopy-900",
            },
            h(
              "span",
              { className: "text-sm font-semibold" },
              `${t("price.total")}*`
            ),
            h(
              "div",
              { className: "flex items-center gap-2" },
              h(
                "div",
                { className: "text-right" },
                h(
                  "strong",
                  { className: "text-lg text-brand-700 font-bold" },
                  amountToHuman(gross)
                ),
                this.state.hasIncludedTaxes &&
                  h(
                    "div",
                    { className: "text-xs text-brandcopy-600" },
                    t("price.details.tax_included")
                  )
              ),
              h(PriceDetailsModal, {
                discounts: this.state.discounts,
                days: this.state.days,
                gross: gross,
                rentalTotal: this.state.rentalTotal,
                perNight: this.state.perNight,
                cleaning: this.state.cleaning,
                unit: this.state.unit,
                hasIncludedTaxes: this.state.hasIncludedTaxes,
                kurtaxe: this.state.kurtaxe,
                businessOnly: this.state.businessOnly,
                unitsNeeded: this.state.unitsNeeded,
                persons: this.state.adults + this.state.children,
                dogs: this.state.dogs,
                seasons: this.state.seasons,
                services: this.state.services,
                infoServices: this.state.infoServices,
                hasTourismTax: this.state.hasTourismTax,
                utilities: this.state.utilities,
              })
            )
          ),
          h(
            "div",
            { className: "text-xs text-brandcopy-500 mt-1 leading-tight" },
            t("price.calculation.hint")
          )
        ),
        container
      );
    });
  }

  handleDateChange(e) {
    let dates = e?.currentTarget?._flatpickr?.selectedDates || [];
    if (dates.length < 2) {
      const dp =
        document.querySelector("#checkinCheckoutDates")?._flatpickr ||
        document.querySelector("#booking_dates")?._flatpickr;
      if (dp?.selectedDates?.length === 2) {
        dates = dp.selectedDates;
      }
    }

    if (dates.length < 2 && e?.currentTarget?.value) {
      const val = e.currentTarget.value.trim();
      const parts = val.split(/\s+(?:bis|-)\s+/);
      if (parts.length === 2) {
        const parseDmy = (str) => {
          const clean = str.trim();
          if (clean.includes("-")) {
            const [y, m, d] = clean.split("-").map(Number);
            return y && m && d ? new Date(y, m - 1, d) : null;
          }
          const [d, m, y] = clean.split(".").map(Number);
          return d && m && y ? new Date(y, m - 1, d) : null;
        };
        const d1 = parseDmy(parts[0]);
        const d2 = parseDmy(parts[1]);
        if (d1 && d2) dates = [d1, d2];
      }
    }

    let days = 0;
    if (dates.length === 2) {
      days = differenceInDays(dates[1], dates[0]);
    }
    this.setState({ dates, days }, () => this.calculatePrice());
  }

  handleAdultsChange(e) {
    const val = parseInt(e.currentTarget.value, 10) || 1;
    this.setState({ adults: val }, () => this.calculatePrice());
  }

  handleChildrenChange(e) {
    const val = parseInt(e.currentTarget.value, 10) || 0;
    this.setState({ children: val, childrenAges: [] }, () =>
      this.calculatePrice()
    );
  }

  handleChildrenAgesChange() {
    const childrenAges = Array.from(
      document.querySelectorAll("#booking_ageChildren input")
    ).map((input) => parseInt(input.value, 10) || null);
    this.setState({ childrenAges }, () => this.calculatePrice());
  }

  handleDogsChange(e) {
    const val = parseInt(e.currentTarget.value, 10) || 0;
    this.setState({ dogs: val }, () => this.calculatePrice());
  }

  handleUnitChange(e) {
    const unitId = e.currentTarget.value;
    this.setState({ rentalUnitId: String(unitId) }, () =>
      this.calculatePrice()
    );
  }

  addListeners() {
    const dateEls = document.querySelectorAll(
      "#checkinCheckoutDates, #booking_dates"
    );
    dateEls.forEach((el) => {
      el.addEventListener("change", this.handleDateChange);
    });

    const adultsEls = document.querySelectorAll("#adults, #booking_adults");
    adultsEls.forEach((el) => {
      el.addEventListener("change", this.handleAdultsChange);
      el.addEventListener("input", this.handleAdultsChange);
    });

    const childrenEls = document.querySelectorAll(
      "#children, #booking_children"
    );
    childrenEls.forEach((el) => {
      el.addEventListener("change", this.handleChildrenChange);
      el.addEventListener("input", this.handleChildrenChange);
    });

    this.childrenAgesContainer = document.getElementById("booking_ageChildren");
    this.childrenAgesContainer?.addEventListener(
      "change",
      this.handleChildrenAgesChange
    );
    this.childrenAgesContainer?.addEventListener(
      "input",
      this.handleChildrenAgesChange
    );

    const dogsEls = document.querySelectorAll("#dogs, #booking_dogs");
    dogsEls.forEach((el) => {
      el.addEventListener("change", this.handleDogsChange);
      el.addEventListener("input", this.handleDogsChange);
    });

    const unitEls = document.querySelectorAll(
      "#rentalUnitId, #rental_unit_id, #booking_rental_unit_id, #booking_rentalUnitId"
    );
    unitEls.forEach((el) => {
      el.addEventListener("change", this.handleUnitChange);
    });
  }

  removeListeners() {
    const dateEls = document.querySelectorAll(
      "#checkinCheckoutDates, #booking_dates"
    );
    dateEls.forEach((el) => {
      el.removeEventListener("change", this.handleDateChange);
    });

    const adultsEls = document.querySelectorAll("#adults, #booking_adults");
    adultsEls.forEach((el) => {
      el.removeEventListener("change", this.handleAdultsChange);
      el.removeEventListener("input", this.handleAdultsChange);
    });

    const childrenEls = document.querySelectorAll(
      "#children, #booking_children"
    );
    childrenEls.forEach((el) => {
      el.removeEventListener("change", this.handleChildrenChange);
      el.removeEventListener("input", this.handleChildrenChange);
    });
    this.childrenAgesContainer?.removeEventListener(
      "change",
      this.handleChildrenAgesChange
    );
    this.childrenAgesContainer?.removeEventListener(
      "input",
      this.handleChildrenAgesChange
    );

    const dogsEls = document.querySelectorAll("#dogs, #booking_dogs");
    dogsEls.forEach((el) => {
      el.removeEventListener("change", this.handleDogsChange);
      el.removeEventListener("input", this.handleDogsChange);
    });

    const unitEls = document.querySelectorAll(
      "#rentalUnitId, #rental_unit_id, #booking_rental_unit_id, #booking_rentalUnitId"
    );
    unitEls.forEach((el) => {
      el.removeEventListener("change", this.handleUnitChange);
    });
  }

  render() {
    return null;
  }
}

export default BookingCalculation;
